import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Save, Globe, Mail, Phone, MapPin, Image } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import AdminLayout from '../components/AdminLayout';
import ConfirmModal from '../components/ConfirmModal';

interface Setting {
  _id: string;
  key: string;
  value: string;
  category: string;
  description?: string;
}

const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSetting, setNewSetting] = useState({
    key: '',
    value: '',
    category: 'social',
    description: ''
  });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getAllAdmin();
      setSettings(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (setting: Setting) => {
    try {
      await settingsAPI.update({
        key: setting.key,
        value: setting.value,
        category: setting.category,
        description: setting.description
      });
      toast.success('Setting updated');
      fetchSettings();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update');
    }
  };

  const handleCreate = async () => {
    if (!newSetting.key || !newSetting.value) {
      toast.error('Key and value are required');
      return;
    }
    try {
      await settingsAPI.update(newSetting);
      toast.success('Setting created');
      setNewSetting({ key: '', value: '', category: 'social', description: '' });
      fetchSettings();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create');
    }
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Setting',
      message: 'Are you sure you want to delete this setting? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await settingsAPI.delete(id);
          toast.success('Setting deleted');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchSettings();
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Failed to delete');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'contact': return <Mail className="w-5 h-5" />;
      case 'social': return <Globe className="w-5 h-5" />;
      case 'hero': return <Image className="w-5 h-5" />;
      default: return <MapPin className="w-5 h-5" />;
    }
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    if (!acc[setting.category]) acc[setting.category] = [];
    acc[setting.category].push(setting);
    return acc;
  }, {} as Record<string, Setting[]>);

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Site Settings</h1>

      {/* Add New Setting */}
      <Card className="mb-8 p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add New Setting
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Key"
            placeholder="e.g., social_facebook"
            value={newSetting.key}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSetting({ ...newSetting, key: e.target.value })}
          />
          <Input
            label="Value"
            placeholder="e.g., https://facebook.com/bsaf"
            value={newSetting.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSetting({ ...newSetting, value: e.target.value })}
          />
          <select
            className="px-4 py-2 border rounded-lg"
            value={newSetting.category}
            onChange={(e) => setNewSetting({ ...newSetting, category: e.target.value })}
          >
            <option value="contact">Contact</option>
            <option value="social">Social Media</option>
            <option value="hero">Hero Slider</option>
            <option value="general">General</option>
            <option value="email">Email</option>
          </select>
          <Input
            label="Description"
            placeholder="Optional description"
            value={newSetting.description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSetting({ ...newSetting, description: e.target.value })}
          />
        </div>
        <Button onClick={handleCreate} className="mt-4">
          <Plus className="w-4 h-4 mr-2" />
          Add Setting
        </Button>
      </Card>

      {/* Existing Settings by Category */}
      {Object.entries(groupedSettings).map(([category, items]) => (
        <Card key={category} className="mb-6 p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 capitalize">
            {getCategoryIcon(category)}
            {category} Settings
          </h2>
          <div className="space-y-4">
            {items.map((setting) => (
              <div key={setting._id} className="flex gap-4 items-start p-4 bg-gray-50 rounded-lg">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border rounded-lg bg-gray-100"
                      value={setting.key}
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border rounded-lg"
                      value={setting.value}
                      onChange={(e) => {
                        const updated = settings.map(s =>
                          s._id === setting._id ? { ...s, value: e.target.value } : s
                        );
                        setSettings(updated);
                      }}
                    />
                  </div>
                  {setting.description && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-lg"
                        value={setting.description}
                        onChange={(e) => {
                          const updated = settings.map(s =>
                            s._id === setting._id ? { ...s, description: e.target.value } : s
                          );
                          setSettings(updated);
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdate(setting)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Save"
                  >
                    <Save className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(setting._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {settings.length === 0 && (
        <Card className="p-8 text-center text-gray-500">
          No settings found. Add your first setting above.
        </Card>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        loading={processing}
      />
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
